import { Icon } from "@/components/icons/Icon";
import { DropdownMenu, DropdownMenuItem } from "@/components/interactive/DropdownMenu";
import { SuspendedIndicator } from "@/components/navigation/SuspendedIndicator";
import { EMPTY_ARRAY } from "@/constants/stable-empty";
import { isNonEmptyArray } from "@/utils/array-utils";
import { classNames } from "@/utils/classnames";
import { useRouteAndTeamInfo } from "@/utils/hooks/url/use-route-info";
import { replaceProjectAndEnv } from "@/utils/route-utils";
import { Routes } from "@/utils/routes";
import { useDefaultProject } from "@/utils/storage/id-storage";
import { sortBy } from "lodash";
import { useRouter } from "next/router";

export function ProjectDropdown() {
  const {
    team,
    project: currentProject,
    projectId: currentProjectId,
    environment: currentEnvironment,
  } = useRouteAndTeamInfo();

  const router = useRouter();

  const [defaultProject] = useDefaultProject();

  const projects = team?.projects || EMPTY_ARRAY;

  return currentProject ? (
    <DropdownMenu
      floatOptions={{ placement: "bottom-start" }}
      cardProps={{
        className:
          "sm:min-w-48 rounded-lg divide-y divide-zinc-100 border border-zinc-50 shadow-xl max-h-[calc(100vh-80px)] overflow-y-auto",
      }}
      renderButton={(r, open) => (
        <div className={"flex items-baseline"}>
          <button
            ref={r}
            className={classNames(
              "h-6 py-2",
              "rounded-full",
              "transition-colors duration-75",
              "inline-flex justify-center w-full px-2 py-1 items-center",
              "text-cds-sm font-medium border-b-2 border-transparent",
              "hover:bg-cds-light-gray-100 hover:text-zinc-700 active:bg-cds-light-gray-100 active:text-zinc-800",
              open ? "bg-cds-light-gray-100 text-cds-text-primary" : "text-cds-text-primary",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75",
            )}
          >
            {currentProject ? (
              <div>
                <span>{currentProject.name}</span>
                {currentEnvironment && (
                  <span className={"ml-1 text-cds-text-secondary font-everettlight"}>
                    / {currentEnvironment.name}
                  </span>
                )}
              </div>
            ) : (
              <span>Choose a project</span>
            )}
            <Icon name="chevronDown" className="ml-1 mt-0.5 text-cds-text-secondary" />
          </button>
          {currentEnvironment?.suspendedAt != null && <SuspendedIndicator className={"ml-2"} />}
        </div>
      )}
    >
      <DropdownMenuItem className="text-cds-sm" href={Routes.projects}>
        <Icon name="arrowLeft" size="sm" className="text-cds-text-secondary" /> All Projects
      </DropdownMenuItem>

      {sortBy(projects, (p) => p.name)
        .filter((project) => isNonEmptyArray(project.environments))
        .map((project) => (
          <div key={project.id}>
            {sortBy(project.environments, (e) => e.name).map((environment) => {
              const isDefault =
                defaultProject != null &&
                defaultProject.projectId === project.id &&
                defaultProject.environmentId === environment.id;
              return (
                <DropdownMenuItem
                  key={environment.id}
                  className="sm"
                  current={
                    project.id === currentProjectId && environment.id === currentEnvironment?.id
                  }
                  href={replaceProjectAndEnv(router, project.id, environment.id)}
                >
                  <div className="pl-4 flex items-baseline gap-1">
                    <span className={"text-cds-text-primary"}>{project.name}</span>
                    <span className={"text-cds-text-secondary font-everettlight"}>
                      {" "}
                      / {environment.name}
                    </span>
                    {isDefault && (
                      <Icon name="star" size="sm" className={"ml-2 text-cds-bright-green-500"} />
                    )}
                  </div>
                </DropdownMenuItem>
              );
            })}
          </div>
        ))}
    </DropdownMenu>
  ) : null;
}
